<template>
  <SoDialog
    v-model="value"
    card-title="Estimate Reads"
    :max-width="1000"
    :loading="current.meterpoints.isFetching"
  >
    <SoSelect
      v-if="needsMeterpointChoice"
      v-model="selectedMeterpointId"
      :items="meterpointOptions"
      label="Meter point"
      placeholder="Select a meter point"
      type="simple"
      class="mb-3"
      data-testid="estimator-meterpoint-select"
    />

    <SoTabs v-if="selectedMeterpoint" class="pb-2">
      <SoTab
        v-for="mode in modes"
        :key="mode"
        :title="titleFor(mode)"
        icon="calculator"
      >
        <EstimatorForm :meterpoint="selectedMeterpoint" :mode="mode" />
      </SoTab>
    </SoTabs>
    <p
      v-else-if="needsMeterpointChoice"
      class="text-caption"
      data-testid="estimator-meterpoint-prompt"
    >
      Choose which meter point to estimate for.
    </p>
    <!--
      No meter points and nothing in flight leaves the dialog with nothing in
      it at all, which reads as a broken modal rather than an empty account.
    -->
    <SoAlert
      v-else-if="!current.meterpoints.isFetching"
      type="info"
      text="This account has no meter points, so there is nothing to estimate a read for."
      data-testid="estimator-no-meterpoints"
    />
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import SoTabs from "@/components/tabs/SoTabs.vue"
  import SoTab from "@/components/tabs/SoTab.vue"
  import EstimatorForm from "../components/EstimateReads/EstimatorForm.vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { IMeterpoint } from "@/models/meterpoint/meterpoint.types"
  import {
    ESTIMATOR_MODE_TITLES,
    EstimatorUiMode,
    eacOrAqLabel,
  } from "../components/EstimateReads/estimatorHelper"

  export default defineComponent({
    components: { EstimatorForm, SoAlert, SoDialog, SoSelect, SoTab, SoTabs },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        current: useCurrentAccountStore(),
        selectedMeterpointId: null as number | null,
        modes: Object.values(EstimatorUiMode),
      }
    },
    computed: {
      value: {
        get(): boolean {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
      meterpoints(): IMeterpoint[] {
        return this.current.meterpoints.data
      },
      /**
       * The Meter Readings widget is account-level, so there is no meter point
       * the agent came from. Where the account has more than one, the agent
       * picks before anything else is shown - an estimate against a meter point
       * nobody chose is worse than one extra click. A single meter point needs
       * no asking. Renewals never gets here: it passes the meter point the
       * agent clicked straight to the form.
       */
      needsMeterpointChoice(): boolean {
        return this.meterpoints.length > 1
      },
      selectedMeterpoint(): IMeterpoint | undefined {
        if (!this.needsMeterpointChoice) return this.meterpoints[0]

        return this.meterpoints.find(
          (meterpoint) => meterpoint.id === this.selectedMeterpointId,
        )
      },
      meterpointOptions() {
        return this.meterpoints.map((meterpoint) => ({
          title: `${meterpoint.identifier} (${
            meterpoint.isElectricity ? "Electricity" : "Gas"
          })`,
          value: meterpoint.id,
        }))
      },
    },
    methods: {
      titleFor(mode: EstimatorUiMode): string {
        const label = this.selectedMeterpoint
          ? eacOrAqLabel(this.selectedMeterpoint)
          : "EAC/AC"

        return ESTIMATOR_MODE_TITLES[mode].replace("{eacOrAq}", label)
      },
    },
  })
</script>
